/**
 * Lazy, access-aware loading of the docs search index.
 *
 * The index is built at commit time by `scripts/build-docs-search-index.mjs`
 * into one JSON shard per doc audience (`DOC_AUDIENCES` in
 * `docAccessRules.js`). This module loads only the shards the reader is
 * allowed, resolved through `canViewAudience` — the same predicate the
 * sidebar and `DocsMdxView` use, so a search result can never surface a
 * page the reader would be refused on click.
 *
 * Shards load through `import.meta.glob` deliberately, matching how
 * `DocsMdxView` loads the MDX pages themselves, so the index carries
 * exactly the same exposure as the docs it describes and no new one.
 * Serving them from `public/` would have been simpler and strictly worse —
 * a stable, anonymously fetchable URL holding a grep-able copy of every
 * page.
 *
 * Nothing here loads until someone actually opens search; a reader who
 * never searches pays nothing.
 */
import { canViewAudience } from '../docAccess'
import { DOC_AUDIENCES } from '../docAccessRules'

// Imported directly rather than redeclared here. The reference this was
// ported from had to redeclare the audience list locally so a TypeScript
// type could ride along with it; this project has no types, so there's
// nothing that duplication would buy — importing the one list `docAccess`
// already checks against keeps the two impossible to drift apart.
const shardLoaders = import.meta.glob('../generated/docs-index.*.json', {
    import: 'default'
})

function loaderFor(audience) {
    const suffix = `docs-index.${audience}.json`
    const key = Object.keys(shardLoaders).find((k) => k.endsWith(suffix))
    return key ? shardLoaders[key] : undefined
}

/**
 * In-flight and settled shards, keyed by audience. Promises are cached
 * rather than results so two components searching at once share one fetch
 * instead of racing.
 */
const shardCache = new Map()

function loadShard(audience) {
    const cached = shardCache.get(audience)
    if (cached) return cached

    const loader = loaderFor(audience)
    const promise = loader
        ? loader().then((pages) => pages.map((page) => ({ ...page, audience })))
        : // A missing shard means the index was never built, or an audience
          // was added without a rebuild. Degrade to "this audience
          // contributes no results" rather than breaking search for the
          // shards that did load.
          Promise.resolve([])

    shardCache.set(audience, promise)
    return promise
}

/**
 * Resolved flat arrays, keyed by the sorted, joined set of permitted
 * audiences (e.g. `"any_admin,member,org_admin"`).
 *
 * `docsSearch.js` caches its prepared-chunk work in a WeakMap keyed on the
 * array `loadDocsSearchIndex` returns, so a repeat call for the same user
 * must hand back the *same array instance* or that cache never hits. Two
 * users who happen to share a permitted-audience set legitimately share a
 * cache entry — the shards themselves are already scoped per-audience, not
 * per-user, so there's nothing user-specific being reused here.
 */
const resolvedCache = new Map()

/**
 * Every indexed page `user` is permitted to see. `user` is the value from
 * `useAuth().user`, and may be `null` for a signed-out reader.
 *
 * Callers should treat the result as read-only and cached — repeat calls
 * for the same permitted-audience set reuse the same underlying array
 * instance.
 */
export async function loadDocsSearchIndex(user) {
    const permitted = DOC_AUDIENCES.filter((audience) =>
        canViewAudience(audience, user)
    )
    const cacheKey = permitted.slice().sort().join(',')

    const cached = resolvedCache.get(cacheKey)
    if (cached) return cached

    const promise = Promise.all(permitted.map(loadShard)).then((shards) =>
        shards.flat()
    )
    resolvedCache.set(cacheKey, promise)
    return promise
}

/** Test seam: drop cached shards so a fresh permission set reloads cleanly. */
export function __resetDocsSearchIndexCache() {
    shardCache.clear()
    resolvedCache.clear()
}
